'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PanelTable } from '@content-removal/shared';
import { api } from './api';
import { useStreaming } from './api-context';
import { API_URL } from './env';

/**
 * The panel's live updates, from the API's own event stream.
 *
 * A board that only changes when somebody reloads it is a board that lies all
 * morning; polling it is what emptied the database's connection pool. So the
 * API holds one server-sent stream open (`GET /admin/events`) and says which
 * case moved, and this hook turns that into one call to the screen.
 *
 * Three things it does deliberately:
 *
 * - **Coalesces.** Twenty cases moving in the same second — a sweep, a mail
 *   run — is one refetch, not twenty. A burst is exactly when a screen must
 *   not start twenty requests against a pool of six.
 * - **Waits for quiet.** The callback fires after the burst settles, so a
 *   board redraws once with everything rather than five times in a row.
 * - **Stops when the tab is hidden and catches up when it comes back.** A tab
 *   left open on another desktop needs no live board; the moment it is looked
 *   at again it is refetched once, which is also what makes a dropped stream
 *   recover — `EventSource` reconnects by itself, and the visibility handler
 *   covers the case where the browser froze the whole tab instead.
 */
const SETTLE_MS = 1_200;

/* One connection for the whole panel. Each hook used to open its own, and a
   screen with two boards had four against a limit of six per host — past that
   the browser queues ordinary requests behind the streams and the page stops
   loading rather than stops updating. */
const listeners = new Map<string, Set<(event: MessageEvent) => void>>();
let shared: EventSource | null = null;

function fanOut(name: string) {
  return (event: MessageEvent) => {
    for (const listener of listeners.get(name) ?? []) listener(event);
  };
}

const fans = new Map<string, (event: MessageEvent) => void>();

function listen(name: string, listener: (event: MessageEvent) => void): () => void {
  if (typeof window === 'undefined' || typeof EventSource === 'undefined') return () => {};
  if (!shared) {
    /* `withCredentials`, because the session is an httpOnly cookie and an
       EventSource does not send one otherwise. */
    shared = new EventSource(`${API_URL}/admin/events`, { withCredentials: true });
  }
  if (!listeners.has(name)) {
    listeners.set(name, new Set());
    const fan = fanOut(name);
    fans.set(name, fan);
    shared.addEventListener(name, fan as EventListener);
  }
  listeners.get(name)!.add(listener);

  return () => {
    const set = listeners.get(name);
    set?.delete(listener);
    if (set && set.size === 0) {
      listeners.delete(name);
      const fan = fans.get(name);
      if (fan) shared?.removeEventListener(name, fan as EventListener);
      fans.delete(name);
    }
    if (listeners.size === 0) {
      shared?.close();
      shared = null;
    }
  };
}

export function usePanelStream(
  onChanged: () => void,
  enabled = true,
  /**
   * Which announcement to listen for. A case board wants `case`, which is the
   * default and what this hook was written for; a table watches its own name
   * from `PANEL_TABLE` and is woken by nothing else — a screen listing scans
   * has no reason to re-read because somebody replied to a takedown.
   */
  table?: PanelTable,
): void {
  const latest = useRef(onChanged);
  latest.current = onChanged;
  const streaming = useStreaming();

  useEffect(() => {
    if (!enabled || !streaming || typeof window === 'undefined' || typeof EventSource === 'undefined') return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const settle = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        latest.current();
      }, SETTLE_MS);
    };

    const name = table ? 'table' : 'case';
    const stop = listen(name, (event) => {
      // One SSE event name carries every table, so the name inside decides
      // whose screen this was for.
      if (table) {
        try {
          if ((JSON.parse(event.data) as { table?: string }).table !== table) return;
        } catch {
          return;
        }
      }
      settle();
    });

    const onVisible = () => {
      if (document.visibilityState === 'visible') settle();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (timer) clearTimeout(timer);
      stop();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, streaming, table]);
}


/** Somebody reading a case right now. */
export interface Viewer {
  id: string;
  name: string;
}

const PRESENCE_PATH = '/admin/takedown/presence';

/**
 * Who is in which case, kept current from the same stream.
 *
 * Read once on mount, then patched by `presence` events, which carry the
 * names themselves — presence changes far more often than cases do, and a
 * refetch per arrival would be the polling this all exists to avoid.
 */
export function usePresence(enabled = true): Record<string, Viewer[]> {
  const [who, setWho] = useState<Record<string, Viewer[]>>({});

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    api<Record<string, Viewer[]>>(PRESENCE_PATH)
      .then((answer) => alive && setWho(answer))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [enabled]);

  const onEvent = useCallback((event: MessageEvent<string>) => {
    let payload: { caseId?: string; who?: Viewer[] };
    try {
      payload = JSON.parse(event.data) as { caseId?: string; who?: Viewer[] };
    } catch {
      return;
    }
    if (!payload.caseId) return;
    const seen = payload.who ?? [];
    setWho((was) => {
      const next = { ...was };
      // An empty list is "nobody here", which has to delete the key rather
      // than store an empty array: the board asks `who[caseId]?.length`, and a
      // case that keeps an empty entry for ever is a slow leak in a screen
      // that stays open all day.
      if (seen.length === 0) delete next[payload.caseId as string];
      else next[payload.caseId as string] = seen;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    return listen('presence', onEvent);
  }, [enabled, onEvent]);

  return who;
}

/**
 * "I am in this case", while it is open.
 *
 * A beat rather than a lock: the first thing a lock does is hold a case for
 * somebody who went to lunch. Thirty seconds is half the life the server gives
 * an entry, so one missed beat on a bad connection does not make a person
 * vanish from a colleague's screen.
 *
 * The goodbye is sent with `keepalive` so it survives the tab closing — and
 * the server's own expiry covers every way a browser leaves without one: a
 * sleeping laptop, a crash, a lost network.
 */
const BEAT_MS = 30_000;

export function usePresenceBeat(caseId: string | null): void {
  useEffect(() => {
    if (!caseId) return;
    const here = () => api(`/admin/takedown/cases/${caseId}/here`, { method: 'POST' }).catch(() => undefined);
    here();
    const beat = setInterval(here, BEAT_MS);

    const leave = () => {
      try {
        void fetch(`${API_URL}/admin/takedown/cases/${caseId}/gone`, {
          method: 'POST',
          credentials: 'include',
          keepalive: true,
        });
      } catch {
        /* the server's own expiry is the backstop */
      }
    };
    window.addEventListener('pagehide', leave);

    return () => {
      clearInterval(beat);
      window.removeEventListener('pagehide', leave);
      leave();
    };
  }, [caseId]);
}
